import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, Trash2, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Rate-a-show widget for ShowDetail. Renders:
 *  - Public aggregate (avg + count + distribution bars) — always shown
 *  - User rating controls — only if signed in AND user has watched the show at
 *    least once. Otherwise a helpful nudge is shown instead.
 */
export default function ShowRatingBox({ showId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(0);
  const [saving, setSaving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewText, setReviewText] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/shows/${showId}/rating`);
      setData(data);
      setReviewText(data?.my_rating?.review || "");
    } catch {
      setData({ avg: 0, count: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, my_rating: null, eligible_to_rate: false });
    }
  };

  useEffect(() => { load(); }, [showId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (stars) => {
    if (!user) {
      toast("Sign in to rate", { description: "Continue with Discord to rate shows." });
      return;
    }
    if (!data?.eligible_to_rate) {
      toast("Watch it first", { description: "Mark the show as watched, then you can rate it." });
      return;
    }
    setSaving(true);
    try {
      const { data: resp } = await api.put(`/user/shows/${showId}/rating`, { stars, review: reviewText });
      setData((prev) => ({ ...prev, ...resp }));
      toast.success(`Rated ${stars} ★`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save rating");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      const { data: resp } = await api.delete(`/user/shows/${showId}/rating`);
      setData((prev) => ({ ...prev, ...resp, my_rating: null }));
      setReviewText("");
      setReviewOpen(false);
      toast("Rating removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const saveReview = async () => {
    if (!data?.my_rating) {
      toast("Rate the show first, then add a review.");
      return;
    }
    await submit(data.my_rating.stars);
    setReviewOpen(false);
  };

  if (!data) {
    return (
      <div className="surface-card p-6 flex items-center gap-3 text-[var(--ff-text-dim)]" data-testid="rating-box-loading">
        <Loader2 className="animate-spin" size={16} /> Loading ratings…
      </div>
    );
  }

  const total = data.count || 0;
  const currentStars = data.my_rating?.stars || 0;
  const displayStars = hover || currentStars;

  return (
    <div className="surface-card p-6" data-testid="rating-box">
      <div className="flex items-center justify-between mb-4">
        <div className="label-eyebrow text-[var(--ff-red)]">▮ FAN RATING</div>
        <div className="flex items-center gap-2">
          <Star size={16} className="text-[#FFD60A]" fill={total > 0 ? "#FFD60A" : "none"} />
          <span className="font-display text-2xl" data-testid="rating-avg">
            {total > 0 ? data.avg.toFixed(1) : "—"}
          </span>
          <span className="text-xs text-[var(--ff-text-dim)] font-mono-ui" data-testid="rating-count">
            ({total} rating{total === 1 ? "" : "s"})
          </span>
        </div>
      </div>

      {/* Distribution bars (5 -> 1) */}
      {total > 0 && (
        <div className="space-y-1 mb-5" data-testid="rating-distribution">
          {[5, 4, 3, 2, 1].map((s) => {
            const n = data.distribution?.[s] || 0;
            const pct = total > 0 ? Math.round((n / total) * 100) : 0;
            return (
              <div key={s} className="flex items-center gap-2 text-xs">
                <span className="font-mono-ui w-4 text-[var(--ff-text-dim)]">{s}</span>
                <Star size={10} className="text-[#FFD60A]" fill="#FFD60A" />
                <div className="flex-1 bg-black/40 h-1.5">
                  <div className="h-full bg-[#FFD60A]" style={{ width: `${pct}%` }} />
                </div>
                <span className="font-mono-ui w-8 text-right text-[var(--ff-text-dim)]">{n}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Interactive controls */}
      {!user ? (
        <p className="text-xs text-[var(--ff-text-dim)]" data-testid="rating-signin-nudge">
          Sign in with Discord to rate this show.
        </p>
      ) : !data.eligible_to_rate ? (
        <p className="text-xs text-[var(--ff-text-dim)]" data-testid="rating-watch-nudge">
          Mark the show as <span className="text-white">watched</span> at least once to unlock ratings.
        </p>
      ) : (
        <>
          <div
            className="flex items-center gap-1 mb-3"
            onMouseLeave={() => setHover(0)}
            data-testid="rating-star-picker"
          >
            {[1, 2, 3, 4, 5].map((n) => {
              const active = n <= displayStars;
              return (
                <button
                  key={n}
                  disabled={saving}
                  onClick={() => submit(n)}
                  onMouseEnter={() => setHover(n)}
                  aria-label={`Rate ${n} stars`}
                  data-testid={`rating-star-${n}`}
                  className="disabled:opacity-50 transition-transform hover:scale-110"
                >
                  <Star
                    size={26}
                    className={active ? "text-[#FFD60A]" : "text-[var(--ff-text-dim)]"}
                    fill={active ? "#FFD60A" : "none"}
                  />
                </button>
              );
            })}
            {currentStars > 0 && (
              <span className="ml-3 text-xs text-[var(--ff-text-dim)] font-mono-ui" data-testid="rating-your-label">
                Your rating: <span className="text-white">{currentStars} ★</span>
              </span>
            )}
          </div>

          {currentStars > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setReviewOpen((v) => !v)}
                className="label-eyebrow text-[var(--ff-text-dim)] hover:text-white transition-colors"
                data-testid="rating-toggle-review"
              >
                {data.my_rating?.review ? "Edit your review →" : "Add a review →"}
              </button>
              <button
                onClick={remove}
                disabled={saving}
                className="label-eyebrow text-[var(--ff-red)] hover:brightness-110 transition-all inline-flex items-center gap-1"
                data-testid="rating-remove"
              >
                <Trash2 size={11} /> Remove rating
              </button>
            </div>
          )}

          {reviewOpen && currentStars > 0 && (
            <div className="mt-3 space-y-2" data-testid="rating-review-editor">
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value.slice(0, 1000))}
                placeholder="What did you think? (optional, max 1000 chars)"
                className="w-full text-sm p-3 min-h-[80px]"
                data-testid="rating-review-textarea"
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-[var(--ff-text-dim)] font-mono-ui">{reviewText.length}/1000</span>
                <button
                  onClick={saveReview}
                  disabled={saving}
                  className="btn-primary text-xs disabled:opacity-50"
                  data-testid="rating-review-save"
                >
                  {saving ? "Saving…" : "Save review"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
